import React, { useEffect, useRef, useState } from 'react';
import { toBlob } from 'html-to-image';
import { saveImageBlob } from '@/utils/shareCard';
import { X, Download, Share2, Link2, Check, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { shareViaNative } from '@/utils/nativeShare';
import { SHARE_THEMES, getShareTheme } from '@/components/shareThemes';
import { API_URL as API } from '@/utils/apiConfig';

const PREVIEW_W = 300;
const EXPORT_W = 1080;

// The preview IS the exported image (captured at 1080×1350), so they always match.
const CardPreview = React.forwardRef(function CardPreview({ theme, recap, plan }, ref) {
  return (
    <div
      ref={ref}
      style={{
        width: PREVIEW_W, minHeight: PREVIEW_W * 1.25, boxSizing: 'border-box',
        background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.border} 100%)`,
        padding: '26px 24px 18px',
        display: 'flex', flexDirection: 'column', fontFamily: "'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <span style={{ alignSelf: 'flex-start', padding: '4px 12px', borderRadius: 999, background: theme.tagBg, color: theme.accent, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em' }}>
        PLAN RECAP
      </span>
      <p style={{ color: theme.secondary, fontSize: 12, margin: '10px 0 6px' }}>{plan?.title} · {plan?.length_days}-day plan</p>
      <h2 style={{ color: theme.text, fontSize: 18, fontWeight: 700, lineHeight: 1.2, margin: '0 0 10px' }}>{recap.headline}</h2>
      <p style={{ color: theme.accent, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', margin: '0 0 8px' }}>KEY TAKEAWAYS</p>
      <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {(recap.takeaways || []).slice(0, 3).map((tk, i) => (
          <li key={i} style={{ display: 'flex', gap: 7, marginBottom: 6, color: theme.text, fontSize: 9, lineHeight: 1.35 }}>
            <span style={{ flexShrink: 0, width: 15, height: 15, borderRadius: 999, background: theme.accent, color: theme.bg, fontSize: 8.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
            <span>{tk}</span>
          </li>
        ))}
      </ol>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: 'auto 0 10px', paddingTop: 6 }}>
        {(recap.themes || []).slice(0, 4).map((th, i) => (
          <span key={i} style={{ padding: '3px 9px', borderRadius: 999, background: theme.tagBg, color: theme.accent, fontSize: 9 }}>{th}</span>
        ))}
      </div>
      <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <img src="/logo.png" alt="Eili" style={{ width: 20, height: 20 }} />
          <span style={{ fontFamily: "'Playwrite ES Deco', cursive", fontSize: 12, fontWeight: 700, color: theme.accent }}>Eili</span>
        </span>
        <span style={{ fontSize: 9, color: theme.secondary }}>askeili.com</span>
      </div>
    </div>
  );
});

function ThemePicker({ selected, onSelect }) {
  return (
    <div className="flex gap-2.5 flex-wrap" data-testid="share-plan-recap-theme-picker">
      {SHARE_THEMES.map((t) => {
        const active = selected === t.key;
        return (
          <button key={t.key} onClick={() => onSelect(t.key)} className="flex flex-col items-center gap-1.5" data-testid={`share-plan-recap-theme-${t.key}`}>
            <div className="w-10 h-10 rounded-full relative overflow-hidden transition-[box-shadow,border-color]"
              style={{ background: t.swatch[0], border: active ? `2.5px solid ${t.swatch[1]}` : '2px solid transparent', boxShadow: active ? `0 0 0 2px ${t.swatch[1]}40` : 'none' }}>
              <div className="absolute bottom-0 right-0 w-5 h-5 rounded-tl-full" style={{ background: t.swatch[1] }} />
            </div>
            <span className="text-[10px] font-medium" style={{ color: active ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function SharePlanRecapModal({ planId, plan, recap, onClose }) {
  const [themeKey, setThemeKey] = useState('classic');
  const [shareInfo, setShareInfo] = useState(null);
  const [blobs, setBlobs] = useState({});
  const pngBlob = blobs[themeKey] || null;
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const theme = getShareTheme(themeKey);
  const shareUrl = shareInfo ? `${window.location.origin}/api/og/share/${shareInfo.share_id}` : null;

  useEffect(() => {
    axios.post(`${API}/plan-recap/share`, { plan_id: planId, theme: themeKey }, { withCredentials: true })
      .then((r) => setShareInfo(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not create share card'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const cardRef = useRef(null);

  // Pre-bake the PNG from the preview so Share/Save fire instantly.
  useEffect(() => {
    if (blobs[themeKey]) return undefined;
    const key = themeKey;
    let cancelled = false;
    (async () => {
      try {
        await document.fonts?.ready;
        if (cancelled || !cardRef.current) return;
        const opts = { pixelRatio: EXPORT_W / PREVIEW_W, skipAutoScale: true, cacheBust: false };
        await toBlob(cardRef.current, opts); // warm-up pass so fonts/logo are embedded
        const b = await toBlob(cardRef.current, opts);
        if (!cancelled && b) setBlobs((prev) => ({ ...prev, [key]: b }));
      } catch (err) {
        console.warn('[SharePlanRecapModal] render failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeKey]);

  // Remember the chosen style and upload the exact card so link previews match.
  const persistTheme = () => {
    axios.post(`${API}/plan-recap/share`, { plan_id: planId, theme: themeKey }, { withCredentials: true }).catch(() => {});
    if (!shareInfo || !pngBlob) return;
    const form = new FormData();
    form.append('theme', themeKey);
    form.append('file', new File([pngBlob], 'card.png', { type: 'image/png' }));
    axios.put(`${API}/plan-recap/share/${shareInfo.share_id}/card`, form, { withCredentials: true }).catch(() => {});
  };

  const fileName = `eili-plan-recap-${Date.now()}.png`;
  const text = `I just finished "${plan?.title}" with Eili. Here's what I learned — want to try the plan?`;

  const handleShare = async () => {
    setBusy(true);
    persistTheme();
    try {
      const file = new File([pngBlob], fileName, { type: 'image/png' });
      const res = await shareViaNative({ title: 'My plan recap', text, url: shareUrl, file });
      if (!res?.aborted && !res?.shared) toast.info('Use Save or Link to share manually.');
    } catch {
      toast.error('Could not open the share sheet');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = () => saveImageBlob(pngBlob, fileName);


  const handleCopy = async () => {
    persistTheme();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose} data-testid="share-plan-recap-modal">
      <div className="relative w-full max-w-md flex flex-col rounded-2xl overflow-hidden"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)', maxHeight: 'min(92vh, calc(var(--visual-vh, 100vh) - 32px))' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>Share your recap</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full transition-colors hover:bg-white/10" data-testid="share-plan-recap-close">
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <ThemePicker selected={themeKey} onSelect={setThemeKey} />
          <div className="rounded-xl overflow-hidden mx-auto" style={{ width: PREVIEW_W + 2, border: '1px solid var(--glass-border)' }} data-testid="share-plan-recap-preview">
            <CardPreview ref={cardRef} theme={theme} recap={recap} plan={plan} />
          </div>
        </div>
        <div className="px-5 py-4 border-t flex gap-2.5" style={{ borderColor: 'var(--glass-border)' }}>
          <Button onClick={handleShare} disabled={busy || !pngBlob} className="flex-1 rounded-xl py-4 font-medium gap-2" style={{ background: theme.accent, color: '#fff' }} data-testid="share-plan-recap-image-btn">
            {!pngBlob ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Share2 className="h-4 w-4" />}
            {!pngBlob ? 'Preparing…' : busy ? 'Sharing…' : 'Share Image'}
          </Button>
          <Button onClick={handleSave} disabled={busy || !pngBlob} variant="outline" className="rounded-xl py-4 gap-2" style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }} data-testid="share-plan-recap-download-btn">
            <Download className="h-4 w-4" /> Save
          </Button>
          <Button onClick={handleCopy} disabled={!shareUrl || !pngBlob} variant="outline" className="rounded-xl py-4 gap-2" style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }} data-testid="share-plan-recap-copy-link-btn">
            {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? 'Copied' : 'Link'}
          </Button>
        </div>
      </div>
    </div>
  );
}
